import { useState, useEffect } from 'react';
import { RefreshCw, Mail, Clock, Zap, CheckCircle, XCircle, AlertTriangle, BookOpen, ChevronDown, ChevronUp, Info, Save, Settings2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
    fetchEmailStats,
    fetchEmailLogs,
    fetchEmailTimingConfig,
    updateEmailTimingConfig,
    type EmailStats,
    type EmailLogEntry,
    type EmailTimingConfig,
    type EmailLogsResponse,
} from '../../../lib/prompt-admin-api';

// ============================================
// Email type definitions
// ============================================

interface EmailTypeInfo {
    type: string;
    label: string;
    trigger: 'cron' | 'event';
    source: string;
    condition: string;
    timing: string;
    customVariables: string[];
    suggestedSubject: string;
}

// Custom variables available in lifecycle templates
// USER_NAME is automatically included in all lifecycle emails by triggerEmail()
const LIFECYCLE_VARIABLES = [
    { name: 'USER_NAME', description: 'Imie uzytkownika z profilu (dodawane automatycznie do wszystkich lifecycle emaili)' },
];

// Reserved by Resend - NOT available in programmatic template sends
const RESERVED_VARIABLES = [
    { name: 'FIRST_NAME', description: 'ZAREZERWOWANE - niedostepne w templateach lifecycle. Auto-populated tylko w broadcastach z kontaktu.' },
    { name: 'LAST_NAME', description: 'ZAREZERWOWANE - niedostepne w templateach lifecycle. Auto-populated tylko w broadcastach z kontaktu.' },
    { name: 'EMAIL', description: 'ZAREZERWOWANE - adres email. Auto-populated tylko w broadcastach z kontaktu.' },
];

// Custom variables descriptions
const CUSTOM_VARIABLE_DESCRIPTIONS: Record<string, string> = {
    USER_NAME: 'Imie uzytkownika z profilu (string, dodawane automatycznie do lifecycle emaili)',
    CREDITS_REMAINING: 'Pozostale kredyty uzytkownika (number)',
    CREDITS_LIMIT: 'Limit kredytow w planie (number)',
    UPGRADE_URL: 'Link do strony z planami/upgrade (https://app.wysko.pl/?tab=pricing)',
    ACCESS_END_DATE: 'Data konca dostepu po anulowaniu (string, format polski)',
    ROOM_NAME: 'Nazwa pokoju z nieudanej transformacji (string)',
    CREDITS_ADDED: 'Ilosc dodanych kredytow (number, zawsze 2)',
};

const CRON_EMAILS: EmailTypeInfo[] = [
    {
        type: 'welcome',
        label: 'Powitanie',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: 'Nowy uzytkownik',
        timing: 'Event-driven (pg_net) + cron fallback',
        customVariables: [],
        suggestedSubject: 'Witaj w Wysko, {{USER_NAME}}!',
    },
    {
        type: 'onboarding_incomplete_3d',
        label: 'Onboarding niekompletny (3 dni)',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: '0 generacji po 3 dniach',
        timing: '3 dni od rejestracji',
        customVariables: [],
        suggestedSubject: '{{USER_NAME}}, Twoje kredyty czekaja',
    },
    {
        type: 'onboarding_incomplete_7d',
        label: 'Onboarding niekompletny (7 dni)',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: '0 generacji po 7 dniach',
        timing: '7 dni od rejestracji',
        customVariables: [],
        suggestedSubject: 'Ostatnia szansa na darmowe kredyty',
    },
    {
        type: 'onboarding_complete',
        label: 'Onboarding kompletny',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: 'Wykorzystano darmowe kredyty',
        timing: '1-2 dni po ostatniej generacji',
        customVariables: [],
        suggestedSubject: 'Swietna robota! Co dalej?',
    },
    {
        type: 'trial_extension',
        label: 'Przedluzenie triala',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: 'Brak subskrypcji po 2-3 dniach',
        timing: '2-3 dni od rejestracji (+2 kredyty)',
        customVariables: ['CREDITS_ADDED'],
        suggestedSubject: 'Bonus: +{{CREDITS_ADDED}} dodatkowe kredyty!',
    },
    {
        type: 'best_practices',
        label: 'Najlepsze praktyki',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: 'Pierwsza generacja 3-4 dni temu',
        timing: '3-4 dni po pierwszej generacji',
        customVariables: [],
        suggestedSubject: 'Porady: jak uzyskac najlepsze efekty',
    },
    {
        type: 'win_back_after_cancel',
        label: 'Win-back po anulowaniu',
        trigger: 'cron',
        source: 'lifecycle-emails',
        condition: 'Anulowana subskrypcja',
        timing: '7-14 dni po anulowaniu',
        customVariables: [],
        suggestedSubject: 'Tesknilismy! Mamy cos dla Ciebie',
    },
];

const EVENT_EMAILS: EmailTypeInfo[] = [
    {
        type: 'credits_warning_80',
        label: 'Ostrzezenie o kredytach (80%)',
        trigger: 'event',
        source: 'generate-render',
        condition: 'Remaining <= 20% limitu',
        timing: 'Natychmiast po generacji',
        customVariables: ['CREDITS_REMAINING', 'CREDITS_LIMIT'],
        suggestedSubject: 'Zostalo Ci {{CREDITS_REMAINING}} kredytow',
    },
    {
        type: 'credits_exhausted',
        label: 'Kredyty wyczerpane',
        trigger: 'event',
        source: 'generate-render',
        condition: 'Kredyty = 0',
        timing: 'Natychmiast po generacji',
        customVariables: ['UPGRADE_URL'],
        suggestedSubject: 'Wykorzystano wszystkie kredyty',
    },
    {
        type: 'transformation_failed',
        label: 'Blad transformacji',
        trigger: 'event',
        source: 'generate-render',
        condition: 'Oba AI zawiodly',
        timing: 'Natychmiast',
        customVariables: ['ROOM_NAME'],
        suggestedSubject: 'Problem z Twoja transformacja',
    },
    {
        type: 'subscription_cancelled',
        label: 'Subskrypcja anulowana',
        trigger: 'event',
        source: 'lemon-squeezy-webhook',
        condition: 'Webhook cancellation',
        timing: 'Natychmiast po anulowaniu',
        customVariables: ['ACCESS_END_DATE'],
        suggestedSubject: 'Potwierdzenie anulowania subskrypcji',
    },
];

// ============================================
// Component
// ============================================

export function MailingTab() {
    const [stats, setStats] = useState<EmailStats[]>([]);
    const [recentLogs, setRecentLogs] = useState<EmailLogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [logsPage, setLogsPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [showVariables, setShowVariables] = useState(false);
    const [showTiming, setShowTiming] = useState(false);
    const [timingConfig, setTimingConfig] = useState<EmailTimingConfig[]>([]);
    const [timingEdits, setTimingEdits] = useState<Record<string, Partial<EmailTimingConfig>>>({});
    const [savingTiming, setSavingTiming] = useState<string | null>(null);
    const LOGS_PER_PAGE = 20;

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadLogs();
    }, [logsPage]);

    const loadData = async () => {
        setLoading(true);
        try {
            await Promise.all([loadStats(), loadLogs(), loadTimingConfig()]);
        } catch (err) {
            console.error('[MailingTab] Load error:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const result = await fetchEmailStats();
            setStats(result.stats || []);
        } catch (err) {
            console.error('[MailingTab] Stats error:', err);
        }
    };

    const loadLogs = async () => {
        try {
            const result: EmailLogsResponse = await fetchEmailLogs(logsPage, LOGS_PER_PAGE);
            setRecentLogs(result.data || []);
            setTotalLogs(result.pagination.total);
            setTotalPages(result.pagination.totalPages);
        } catch (err) {
            console.error('[MailingTab] Logs error:', err);
        }
    };

    const loadTimingConfig = async () => {
        try {
            const result = await fetchEmailTimingConfig();
            setTimingConfig(result.data || []);
        } catch (err) {
            console.error('[MailingTab] Timing config error:', err);
        }
    };

    const getStatsForType = (type: string): EmailStats => {
        return stats.find(s => s.type === type) || { type, total: 0, successful: 0, failed: 0 };
    };

    const getTimingForType = (type: string): EmailTimingConfig | undefined => {
        return timingConfig.find(t => t.email_type === type);
    };

    const handleTimingEdit = (emailType: string, field: string, value: number | boolean) => {
        setTimingEdits(prev => ({
            ...prev,
            [emailType]: { ...prev[emailType], [field]: value },
        }));
    };

    const handleTimingSave = async (emailType: string) => {
        const edits = timingEdits[emailType];
        if (!edits) return;

        setSavingTiming(emailType);
        try {
            await updateEmailTimingConfig(emailType, edits);
            toast.success(`Timing dla ${emailType} zaktualizowany`);
            setTimingEdits(prev => {
                const next = { ...prev };
                delete next[emailType];
                return next;
            });
            await loadTimingConfig();
        } catch (err) {
            toast.error('Blad zapisu: ' + (err as Error).message);
        } finally {
            setSavingTiming(null);
        }
    };

    const totalSent = stats.reduce((sum, s) => sum + s.total, 0);
    const totalSuccess = stats.reduce((sum, s) => sum + s.successful, 0);
    const totalFailed = stats.reduce((sum, s) => sum + s.failed, 0);

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Mailing - Lifecycle Emails</h2>
                <Button variant="outline" size="sm" onClick={loadData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Odswiez
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <Mail className="w-5 h-5 text-primary" />
                        <span className="text-sm text-muted-foreground">Wyslane</span>
                    </div>
                    <div className="text-3xl font-bold">{totalSent}</div>
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-muted-foreground">Sukces</span>
                    </div>
                    <div className="text-3xl font-bold text-green-500">{totalSuccess}</div>
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-muted-foreground">Bledy</span>
                    </div>
                    <div className="text-3xl font-bold text-red-500">{totalFailed}</div>
                </div>
            </div>

            {/* Cron Emails Table */}
            <div>
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Emaile cron (co godzine)
                </h3>
                <div className="bg-card border border-border rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="text-left p-3 text-sm font-medium">Typ</th>
                                <th className="text-left p-3 text-sm font-medium">Warunek</th>
                                <th className="text-left p-3 text-sm font-medium">Timing</th>
                                <th className="text-center p-3 text-sm font-medium">Wyslane</th>
                                <th className="text-center p-3 text-sm font-medium">Sukces</th>
                                <th className="text-center p-3 text-sm font-medium">Bledy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {CRON_EMAILS.map(email => {
                                const s = getStatsForType(email.type);
                                const timing = getTimingForType(email.type);
                                return (
                                    <tr key={email.type} className={`border-t border-border ${timing && !timing.enabled ? 'opacity-50' : ''}`}>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm font-medium">{email.label}</div>
                                                {timing && !timing.enabled && (
                                                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">wylaczony</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground font-mono">{email.type}</div>
                                        </td>
                                        <td className="p-3 text-sm text-muted-foreground">{email.condition}</td>
                                        <td className="p-3 text-sm text-muted-foreground">
                                            {timing ? `${timing.min_delay_hours}h - ${timing.max_delay_hours}h` : email.timing}
                                        </td>
                                        <td className="p-3 text-sm text-center font-medium">{s.total}</td>
                                        <td className="p-3 text-sm text-center text-green-500">{s.successful}</td>
                                        <td className="p-3 text-sm text-center text-red-500">{s.failed || '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Event Emails Table */}
            <div>
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Emaile event-driven (natychmiastowe)
                </h3>
                <div className="bg-card border border-border rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="text-left p-3 text-sm font-medium">Typ</th>
                                <th className="text-left p-3 text-sm font-medium">Zrodlo</th>
                                <th className="text-left p-3 text-sm font-medium">Warunek</th>
                                <th className="text-center p-3 text-sm font-medium">Wyslane</th>
                                <th className="text-center p-3 text-sm font-medium">Sukces</th>
                                <th className="text-center p-3 text-sm font-medium">Bledy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {EVENT_EMAILS.map(email => {
                                const s = getStatsForType(email.type);
                                return (
                                    <tr key={email.type} className="border-t border-border">
                                        <td className="p-3">
                                            <div className="text-sm font-medium">{email.label}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{email.type}</div>
                                        </td>
                                        <td className="p-3 text-sm text-muted-foreground font-mono">{email.source}</td>
                                        <td className="p-3 text-sm text-muted-foreground">{email.condition}</td>
                                        <td className="p-3 text-sm text-center font-medium">{s.total}</td>
                                        <td className="p-3 text-sm text-center text-green-500">{s.successful}</td>
                                        <td className="p-3 text-sm text-center text-red-500">{s.failed || '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Timing Editor */}
            <div>
                <button
                    onClick={() => setShowTiming(!showTiming)}
                    className="flex items-center gap-2 text-lg font-medium mb-3 hover:text-primary transition-colors w-full text-left"
                >
                    <Settings2 className="w-5 h-5" />
                    Timing emaili cron
                    {showTiming ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                </button>

                {showTiming && (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="text-left p-3 text-sm font-medium">Typ emaila</th>
                                    <th className="text-center p-3 text-sm font-medium">Min delay (h)</th>
                                    <th className="text-center p-3 text-sm font-medium">Max delay (h)</th>
                                    <th className="text-center p-3 text-sm font-medium">Wlaczony</th>
                                    <th className="text-center p-3 text-sm font-medium">Akcja</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timingConfig.map(config => {
                                    const edits = timingEdits[config.email_type] || {};
                                    const hasEdits = Object.keys(edits).length > 0;
                                    const emailInfo = CRON_EMAILS.find(e => e.type === config.email_type);
                                    return (
                                        <tr key={config.email_type} className="border-t border-border">
                                            <td className="p-3">
                                                <div className="text-sm font-medium">{emailInfo?.label || config.email_type}</div>
                                                <div className="text-xs text-muted-foreground font-mono">{config.email_type}</div>
                                            </td>
                                            <td className="p-3 text-center">
                                                <input
                                                    type="number"
                                                    className="w-20 text-center bg-muted border border-border rounded px-2 py-1 text-sm"
                                                    value={edits.min_delay_hours ?? config.min_delay_hours}
                                                    onChange={(e) => handleTimingEdit(config.email_type, 'min_delay_hours', parseFloat(e.target.value))}
                                                    min={0}
                                                    step={1}
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <input
                                                    type="number"
                                                    className="w-20 text-center bg-muted border border-border rounded px-2 py-1 text-sm"
                                                    value={edits.max_delay_hours ?? config.max_delay_hours}
                                                    onChange={(e) => handleTimingEdit(config.email_type, 'max_delay_hours', parseFloat(e.target.value))}
                                                    min={0}
                                                    step={1}
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 accent-primary"
                                                    checked={edits.enabled ?? config.enabled}
                                                    onChange={(e) => handleTimingEdit(config.email_type, 'enabled', e.target.checked)}
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!hasEdits || savingTiming === config.email_type}
                                                    onClick={() => handleTimingSave(config.email_type)}
                                                >
                                                    {savingTiming === config.email_type ? (
                                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <Save className="w-3 h-3" />
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {timingConfig.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="p-8 text-center text-muted-foreground text-sm">
                                            Brak konfiguracji timingu. Zastosuj migracje bazy danych.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Recent Logs */}
            <div>
                <h3 className="text-lg font-medium mb-3">Ostatnie wysylki</h3>
                <div className="bg-card border border-border rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="text-left p-3 text-sm font-medium">Data</th>
                                <th className="text-left p-3 text-sm font-medium">Uzytkownik</th>
                                <th className="text-left p-3 text-sm font-medium">Typ emaila</th>
                                <th className="text-center p-3 text-sm font-medium">Status</th>
                                <th className="text-left p-3 text-sm font-medium">Resend ID</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentLogs.map(log => (
                                <tr key={log.id} className="border-t border-border">
                                    <td className="p-3 text-sm text-muted-foreground">
                                        {new Date(log.sent_at).toLocaleString('pl-PL')}
                                    </td>
                                    <td className="p-3 text-sm">
                                        {(log.profiles as any)?.email || log.user_id.slice(0, 8) + '...'}
                                    </td>
                                    <td className="p-3">
                                        <span className="text-xs font-mono bg-muted px-2 py-1 rounded">
                                            {log.email_type}
                                        </span>
                                    </td>
                                    <td className="p-3 text-center">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                            log.status === 'sent'
                                                ? 'bg-green-500/20 text-green-500'
                                                : 'bg-red-500/20 text-red-500'
                                        }`}>
                                            {log.status === 'sent' ? (
                                                <CheckCircle className="w-3 h-3" />
                                            ) : (
                                                <AlertTriangle className="w-3 h-3" />
                                            )}
                                            {log.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-xs text-muted-foreground font-mono">
                                        {log.resend_message_id?.slice(0, 16) || '-'}
                                    </td>
                                </tr>
                            ))}
                            {recentLogs.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-muted-foreground text-sm">
                                        Brak wyslanych emaili
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-4">
                        <Button variant="outline" size="sm" disabled={logsPage === 1} onClick={() => setLogsPage(p => p - 1)}>
                            Poprzednia
                        </Button>
                        <span className="px-4 py-2 text-sm">Strona {logsPage} z {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={logsPage === totalPages} onClick={() => setLogsPage(p => p + 1)}>
                            Nastepna
                        </Button>
                    </div>
                )}
            </div>

            {/* Template Variables Reference */}
            <div>
                <button
                    onClick={() => setShowVariables(!showVariables)}
                    className="flex items-center gap-2 text-lg font-medium mb-3 hover:text-primary transition-colors w-full text-left"
                >
                    <BookOpen className="w-5 h-5" />
                    Zmienne szablonow (referencja)
                    {showVariables ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
                </button>

                {showVariables && (
                    <div className="space-y-6">
                        {/* Lifecycle auto variable */}
                        <div className="bg-card border border-border rounded-xl p-5">
                            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4 text-green-500" />
                                Zmienne lifecycle (dostepne we WSZYSTKICH lifecycle emailach)
                            </h4>
                            <p className="text-xs text-muted-foreground mb-3">
                                Te zmienne sa automatycznie dodawane przez triggerEmail() do kazdego lifecycle emaila.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {LIFECYCLE_VARIABLES.map(v => (
                                    <div key={v.name} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                                        <code className="text-xs font-mono bg-green-500/10 text-green-500 px-2 py-1 rounded">
                                            {'{{{' + v.name + '}}}'}
                                        </code>
                                        <span className="text-xs text-muted-foreground">{v.description}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Reserved variables warning */}
                        <div className="bg-card border border-border rounded-xl p-5">
                            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                Zmienne zarezerwowane (NIE uzywaj w lifecycle templateach)
                            </h4>
                            <p className="text-xs text-muted-foreground mb-3">
                                Te nazwy sa zarezerwowane przez Resend. Nie mozna ich uzywac jako zmienne w templateach programmatic (lifecycle).
                                Dzialaja TYLKO w broadcastach (z danych kontaktu Resend).
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {RESERVED_VARIABLES.map(v => (
                                    <div key={v.name} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                                        <code className="text-xs font-mono bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded line-through">
                                            {'{{{' + v.name + '}}}'}
                                        </code>
                                        <span className="text-xs text-muted-foreground">{v.description}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Per-template custom variables */}
                        <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="text-left p-3 text-sm font-medium">Typ emaila</th>
                                        <th className="text-left p-3 text-sm font-medium">Sugerowany subject</th>
                                        <th className="text-left p-3 text-sm font-medium">Zmienne custom</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...CRON_EMAILS, ...EVENT_EMAILS].map(email => (
                                        <tr key={email.type} className="border-t border-border">
                                            <td className="p-3">
                                                <div className="text-sm font-medium">{email.label}</div>
                                                <div className="text-xs text-muted-foreground font-mono">{email.type}</div>
                                            </td>
                                            <td className="p-3">
                                                <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                                                    {email.suggestedSubject}
                                                </code>
                                            </td>
                                            <td className="p-3">
                                                {email.customVariables.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {email.customVariables.map(v => (
                                                            <div key={v} className="flex items-start gap-2">
                                                                <code className="text-xs font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded whitespace-nowrap">
                                                                    {'{{{' + v + '}}}'}
                                                                </code>
                                                                <span className="text-xs text-muted-foreground">
                                                                    {CUSTOM_VARIABLE_DESCRIPTIONS[v] || ''}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">Tylko USER_NAME (auto)</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
